import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Calendar,
  User,
  ArrowLeft,
  Clock,
  Tag,
  Share2,
  BookmarkPlus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Mail,
  Send,
  Twitter
} from "lucide-react";
import HomeNav from "../navigations/homenav";
import Footer from "../navigations/Footer";
import axios from "axios";

const NewsDetail = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animatePage, setAnimatePage] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);

  useEffect(() => {
    setAnimatePage(true);
    fetchNewsDetail();
    fetchRelatedNews();

    // Close share options when clicking outside
    const handleClickOutside = (event) => {
      if (showShareOptions && !event.target.closest('.share-dropdown')) {
        setShowShareOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [id, showShareOptions]);

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://aaapd.org.et/gallery_backend/news/getNewsDetail.php?id=${id}`
      );
      setNews(res.data);
    } catch (err) {
      console.error("Error fetching news detail:", err);
      setError("Failed to load news article. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedNews = async () => {
    try {
      const res = await axios.get(
        `https://aaapd.org.et/gallery_backend/news/getRelatedNews.php?id=${id}`
      );
      setRelatedNews(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching related news:", err);
    }
  };

  const handleShare = (platform) => {
    if (!news) return;

    const url = window.location.href;
    const title = news.title;
    const description = news.excerpt || news.description?.substring(0, 100) + '...';

    switch (platform) {
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + '\n\n' + url)}`;
        break;
      default:
        break;
    }
    setShowShareOptions(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <HomeNav />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-500 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Loading news article...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <HomeNav />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Article Not Found</h2>
            <p className="text-gray-600 mb-6">{error || "The requested news article could not be found."}</p>
            <Link
              to="/news"
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to News
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 min-h-screen flex flex-col transition-opacity duration-1000 ease-in-out ${
      animatePage ? "opacity-100" : "opacity-0"
    }`}>
      <Helmet>
        <title>{news.title} | AAAPD News</title>
        <meta name="description" content={news.excerpt} />
      </Helmet>

      <HomeNav />

      {/* Back Button */}
      <div className="px-6 mt-8">
        <Link
          to="/news"
          className="inline-flex items-center text-teal-600 hover:text-teal-800 font-medium transition-colors duration-200 group"
        >
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
          Back to News
        </Link>
      </div>

      {/* News Detail */}
      <main className="px-6 py-8">
        <article className="bg-white rounded-xl shadow-lg overflow-hidden mx-auto max-w-6xl">
          {/* Article Header */}
          <header className="p-8 pb-6">
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-teal-100 text-teal-800 border border-teal-200">
                <Tag className="w-4 h-4 mr-2" />
                {news.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {news.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-teal-500" />
                <span className="font-medium">{new Date(news.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2 text-teal-500" />
                <span className="font-medium">{news.author}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-teal-500" />
                <span>{Math.ceil(news.description?.length / 200)} min read</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4 mb-8 relative">
              <button className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200">
                <BookmarkPlus className="w-4 h-4 mr-2" />
                Save Article
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowShareOptions(!showShareOptions)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </button>

                {/* Share Options Dropdown */}
                {showShareOptions && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 share-dropdown">
                    <button
                      onClick={() => handleShare('telegram')}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
                    >
                      <Send className="w-5 h-5 text-blue-500" />
                      <span>Share on Telegram</span>
                    </button>
                    <button
                      onClick={() => handleShare('email')}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
                    >
                      <Mail className="w-5 h-5 text-red-500" />
                      <span>Share via Email</span>
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
                    >
                      <Twitter className="w-5 h-5 text-sky-500" />
                      <span>Share on Twitter</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="px-8 pb-8">
            <div className="relative overflow-hidden rounded-lg bg-gray-100">
              <img
                src={news.image}
                alt={news.title}
                className="w-full h-[400px] md:h-[500px] object-cover hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>

          {/* Article Content */}
          <div className="px-8 pb-8">
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                {news.description}
              </div>
            </div>
          </div>
        </article>

        {/* Related News Section */}
        {relatedNews.length > 0 && (
          <section className="mt-16">
            <div className="bg-white rounded-xl shadow-lg p-8 mx-auto max-w-7xl">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <div className="w-1 h-8 bg-teal-500 rounded-full mr-4" />
                Related Articles
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedNews.map((article) => (
                  <Link
                    to={`/news/${article.id}`}
                    key={article.id}
                    className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {article.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(article.date).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default NewsDetail;
